import { v4 as uuidv4 } from 'uuid';

const ENTITY_COLLECTIONS = [
  'categories',
  'items',
  'modifierGroups',
  'ingredients',
];

const externalIdsOf = (record) =>
  new Set(
    Object.values(record || {})
      .map((entity) => entity?.ocoExternalId)
      .filter(Boolean)
  );

const withId = (collection, key, entity) => {
  if (entity._id) return entity;
  if (!entity.ocoExternalId) {
    throw new Error(
      `${collection}["${key}"] has neither "_id" nor "ocoExternalId".`
    );
  }
  return { ...entity, _id: entity.ocoExternalId };
};

/**
 * pos-mms `/menu-import/map` upserts every entity by `ocoExternalId` alone,
 * across all menus, and the mapper sends `_id` when `ocoExternalId` is empty.
 * An external id that does not already belong to the target menu would
 * therefore match, and move, another business's records. Such ids are
 * replaced with fresh ones; ids already in the target menu are kept so a
 * re-import updates those records in place.
 *
 * References inside the map are by `_id`, so they stay valid. An entity
 * without `_id` is referenced by its original `ocoExternalId`, which becomes
 * its `_id` before the external id is replaced. An entity with neither throws.
 *
 * @param {object} importMap  MenuMapDto being imported
 * @param {object} targetMap  current MenuMapDto of the target business
 * @returns {object} copy of importMap with scoped external ids
 */
export const scopeExternalIdsToTargetMenu = (importMap, targetMap) => {
  const scoped = { ...importMap };
  ENTITY_COLLECTIONS.forEach((collection) => {
    const targetIds = externalIdsOf(targetMap?.[collection]);
    scoped[collection] = Object.fromEntries(
      Object.entries(importMap?.[collection] || {}).map(([key, entity]) => {
        const identified = withId(collection, key, entity);
        const externalId = identified.ocoExternalId || identified._id;
        return [
          key,
          {
            ...identified,
            ocoExternalId: targetIds.has(externalId) ? externalId : uuidv4(),
          },
        ];
      })
    );
  });
  return scoped;
};
